/**
 * UnifiedDocumentPanel Component
 *
 * Collapsible side panel for entity-first document processing.
 * Uses documents3 API for graph-only storage.
 * Designed for use in Canvas, Discovery, and AI Coach.
 *
 * Features:
 * - Document upload with entity extraction
 * - Recent documents list with entity counts
 * - Entity preview grouped by domain
 * - Entity detail with evidence quotes
 */

import { useState, useEffect, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  ChevronLeft,
  ChevronRight,
  FileUp,
  FileText,
  Sparkles,
  Clock,
  CheckCircle,
} from 'lucide-react'
import {
  type Document2WithEntities,
  type Document2Entity,
  type Document2ListItem,
} from '@/lib/api/client'
import { useDocuments3Processing } from '@/hooks/useDocuments3Processing'
import { Documents3UploaderCompact } from './Documents3UploaderCompact'
import { EntityListCompact } from './EntityListCompact'
import { EntityDetailCompact } from './EntityDetailCompact'
import { cn } from '@/lib/utils'

// ============================================================================
// TYPES
// ============================================================================

interface UnifiedDocumentPanelProps {
  /** Source context */
  sourceContext: 'canvas' | 'discovery' | 'coach'

  /** Is panel expanded */
  isExpanded: boolean

  /** Toggle expansion */
  onToggleExpand: () => void

  /** Callback when document is analyzed */
  onDocumentAnalyzed?: (result: Document2WithEntities) => void

  /** Callback when entity is selected */
  onEntitySelected?: (entity: Document2Entity, doc: Document2WithEntities) => void

  /** Panel width when expanded */
  expandedWidth?: number

  /** Show keyboard shortcut hint */
  showKeyboardHint?: boolean

  className?: string
}

// ============================================================================
// DOCUMENT LIST ITEM
// ============================================================================

interface DocumentListItemProps {
  doc: Document2ListItem
  isSelected: boolean
  onClick: () => void
}

function DocumentListItem({ doc, isSelected, onClick }: DocumentListItemProps) {
  return (
    <div
      className={cn(
        'p-3 bg-white border rounded-lg cursor-pointer transition-colors',
        isSelected
          ? 'border-solution bg-solution/5'
          : 'border-warm-200 hover:border-warm-300'
      )}
      onClick={onClick}
    >
      <div className="flex items-start gap-3">
        <FileText className="w-4 h-4 text-text-secondary flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-text-primary truncate">
            {doc.title}
          </p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-text-tertiary flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {new Date(doc.createdAt).toLocaleDateString()}
            </span>
            {doc.entityCount > 0 && (
              <Badge variant="secondary" className="text-xs py-0 px-1.5">
                {doc.entityCount} entities
              </Badge>
            )}
            {doc.status === 'analyzed' && (
              <CheckCircle className="w-3 h-3 text-green-500" />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function UnifiedDocumentPanel({
  sourceContext: _sourceContext, // Kept for API compatibility
  isExpanded,
  onToggleExpand,
  onDocumentAnalyzed,
  onEntitySelected,
  expandedWidth = 380,
  showKeyboardHint = true,
  className = '',
}: UnifiedDocumentPanelProps) {
  const [view, setView] = useState<'upload' | 'recent' | 'detail'>('upload')
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null)

  const {
    documents,
    selectedDoc,
    selectDocument,
    refreshDocuments,
  } = useDocuments3Processing({
    autoLoadDocuments: isExpanded,
  })

  // Keyboard shortcut: Cmd/Ctrl+D
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'd') {
        e.preventDefault()
        onToggleExpand()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onToggleExpand])

  const handleDocumentComplete = useCallback((doc: Document2WithEntities) => {
    refreshDocuments()
    selectDocument(doc.document.id)
    setView('detail')
    onDocumentAnalyzed?.(doc)
  }, [refreshDocuments, selectDocument, onDocumentAnalyzed])

  const handleDocumentClick = useCallback((docId: string) => {
    selectDocument(docId)
    setSelectedEntityId(null)
    setView('detail')
  }, [selectDocument])

  const handleEntitySelect = useCallback((entityId: string) => {
    setSelectedEntityId(entityId)
    if (selectedDoc && onEntitySelected) {
      const entity = selectedDoc.entities.find(e => e.id === entityId)
      if (entity) {
        onEntitySelected(entity, selectedDoc)
      }
    }
  }, [selectedDoc, onEntitySelected])

  const handleViewInDocuments3 = useCallback(() => {
    if (selectedDoc) {
      window.location.href = `/documents3?id=${selectedDoc.document.id}`
    }
  }, [selectedDoc])

  const selectedEntity = selectedEntityId && selectedDoc
    ? selectedDoc.entities.find(e => e.id === selectedEntityId)
    : null

  const collapsedWidth = 48

  return (
    <div
      className={cn(
        'h-full bg-warm-50 border-l border-warm-200 transition-all duration-300 flex flex-col',
        className
      )}
      style={{ width: isExpanded ? expandedWidth : collapsedWidth }}
    >
      {/* Collapsed State */}
      {!isExpanded && (
        <div className="flex flex-col items-center py-4 gap-4">
          <button
            onClick={onToggleExpand}
            className="p-2 hover:bg-warm-100 rounded-lg transition-colors"
            title="Open Documents (Cmd+D)"
          >
            <FileText className="h-5 w-5 text-text-secondary" />
          </button>

          {showKeyboardHint && (
            <div className="text-xs text-text-tertiary writing-mode-vertical transform rotate-180">
              Cmd+D
            </div>
          )}
        </div>
      )}

      {/* Expanded State */}
      {isExpanded && (
        <>
          {/* Header */}
          <div className="p-4 border-b border-warm-200 bg-warm-50/80 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-solution" />
              <h3 className="font-semibold text-text-primary">Documents</h3>
            </div>
            <button
              onClick={onToggleExpand}
              className="p-1 hover:bg-warm-100 rounded transition-colors"
              title="Close panel"
            >
              <ChevronRight className="h-4 w-4 text-text-secondary" />
            </button>
          </div>

          {/* View Tabs */}
          <div className="flex border-b border-warm-200 bg-white">
            <button
              onClick={() => setView('upload')}
              className={cn(
                'flex-1 py-2 px-3 text-sm font-medium transition-colors',
                view === 'upload'
                  ? 'text-solution border-b-2 border-solution bg-solution/5'
                  : 'text-text-secondary hover:text-text-primary hover:bg-warm-50'
              )}
            >
              <FileUp className="h-4 w-4 inline mr-1" />
              Upload
            </button>
            <button
              onClick={() => setView('recent')}
              className={cn(
                'flex-1 py-2 px-3 text-sm font-medium transition-colors',
                view === 'recent'
                  ? 'text-solution border-b-2 border-solution bg-solution/5'
                  : 'text-text-secondary hover:text-text-primary hover:bg-warm-50'
              )}
            >
              <FileText className="h-4 w-4 inline mr-1" />
              Recent
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {view === 'upload' && (
              <Documents3UploaderCompact
                onComplete={handleDocumentComplete}
              />
            )}

            {view === 'recent' && (
              <div className="space-y-2">
                {documents.length === 0 ? (
                  <div className="text-center py-8 text-text-tertiary">
                    <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No documents yet</p>
                    <Button
                      variant="link"
                      className="text-solution mt-2"
                      onClick={() => setView('upload')}
                    >
                      Upload your first document
                    </Button>
                  </div>
                ) : (
                  documents.slice(0, 20).map((doc) => (
                    <DocumentListItem
                      key={doc.id}
                      doc={doc}
                      isSelected={selectedDoc?.document.id === doc.id}
                      onClick={() => handleDocumentClick(doc.id)}
                    />
                  ))
                )}
              </div>
            )}

            {view === 'detail' && selectedDoc && (
              <div className="space-y-4">
                {/* Back button */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setView('recent')
                    setSelectedEntityId(null)
                  }}
                  className="gap-1 -ml-2"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Back
                </Button>

                {/* Document Info */}
                <div className="bg-white border border-warm-200 rounded-lg p-3">
                  <h4 className="font-semibold text-text-primary text-sm mb-2 line-clamp-2">
                    {selectedDoc.document.title}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary" className="text-xs">
                      {selectedDoc.entities.length} entities
                    </Badge>
                    {selectedDoc.relationships.length > 0 && (
                      <Badge variant="outline" className="text-xs">
                        {selectedDoc.relationships.length} relationships
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Entity List or Detail */}
                {selectedEntity ? (
                  <div className="space-y-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedEntityId(null)}
                      className="gap-1 -ml-2"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      All Entities
                    </Button>
                    <EntityDetailCompact
                      entity={selectedEntity}
                      onViewInDocuments3={handleViewInDocuments3}
                    />
                  </div>
                ) : (
                  <div className="space-y-2">
                    <h5 className="text-sm font-medium text-text-secondary flex items-center gap-2">
                      <Sparkles className="h-4 w-4" />
                      Extracted Entities
                    </h5>
                    <EntityListCompact
                      entities={selectedDoc.entities}
                      selectedEntityId={selectedEntityId}
                      onEntitySelect={handleEntitySelect}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
